import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmailAuthProvider, linkWithCredential, updatePassword } from "firebase/auth";
import { Check, Loader2, X } from "lucide-react";
import { checkDisplayName, checkPasswordStrength, DISPLAY_NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, type PasswordWeakness } from "@nexus/contracts";
import { auth } from "@/lib/firebase";
import { config } from "@/config";
import { Sentry } from "@/lib/sentry";
import { PasswordStrengthMeter } from "@/features/auth/components/PasswordStrengthMeter";
import { AdgeLogo, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, cn, Input, Label, PasswordInput } from "@nexus/ui";

interface SetPasswordFormProps {
  onComplete: (password: string) => void | Promise<void>;
  /** Invitee email — used to prefill the display name from its local-part. */
  email?: string;
}

const WEAKNESS_MESSAGE_KEYS: Record<Exclude<PasswordWeakness, "tooShort" | "tooLong">, string> = {
  common: "auth.passwordCommon",
  repeated: "auth.passwordRepeated",
  sequential: "auth.passwordSequential",
};

const NAME_PROBLEM_MESSAGE_KEYS = {
  required: "auth.nameRequired",
  tooLong: "auth.nameTooLong",
  noLetter: "auth.nameNoLetter",
} as const;

function RuleRow({ met, label }: { met: boolean; label: string }) {
  const { t } = useTranslation();
  const iconTransition =
    "absolute size-3.5 transition-all duration-200 ease-out motion-reduce:transition-none";
  return (
    <li className="flex items-center gap-1.5 text-xs">
      <span className="relative inline-flex size-3.5 shrink-0 items-center justify-center">
        <X
          className={cn(iconTransition, "text-[var(--muted-foreground)]", met ? "scale-50 opacity-0" : "scale-100 opacity-100")}
          aria-hidden="true"
        />
        <Check
          className={cn(iconTransition, "text-success", met ? "scale-100 opacity-100" : "scale-50 opacity-0")}
          aria-hidden="true"
        />
      </span>
      <span
        className={cn(
          "transition-colors duration-200 ease-out motion-reduce:transition-none",
          met ? "text-success" : "text-[var(--muted-foreground)]",
        )}
      >
        {t(label)}
      </span>
    </li>
  );
}

/** Default display name derived from an email's local-part (`foo@x.com` → `foo`). */
function nameFromEmail(email: string | undefined): string {
  return email ? email.split("@")[0] ?? "" : "";
}

export function SetPasswordForm({ onComplete, email }: SetPasswordFormProps) {
  const { t } = useTranslation();
  // Prefill the name from the invite email's local-part (editable). Seeded on
  // first render; the effect below re-seeds if `email` arrives after mount,
  // but only until the user starts editing.
  const [displayName, setDisplayName] = useState(() => nameFromEmail(email));
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);

  // Keep the prefill in sync with a late-arriving email, but never clobber a
  // value the user has begun editing.
  useEffect(() => {
    if (!nameTouched) setDisplayName(nameFromEmail(email));
  }, [email, nameTouched]);

  const nameProblem = checkDisplayName(displayName);
  const nameErrorKey = nameProblem ? NAME_PROBLEM_MESSAGE_KEYS[nameProblem] : null;
  const showNameError = nameTouched && nameErrorKey !== null;

  const rules = [
    { key: "length", label: "auth.ruleMinLength", met: password.length >= PASSWORD_MIN_LENGTH },
    {
      key: "strength",
      label: "auth.ruleNotCommon",
      met: password.length >= PASSWORD_MIN_LENGTH && checkPasswordStrength(password) === null,
    },
    { key: "match", label: "auth.rulePasswordsMatch", met: confirm.length > 0 && password === confirm },
  ];

  async function setPasswordViaBackend(
    firebaseToken: string,
    newPassword: string,
    name: string,
  ): Promise<{ ok: true } | { ok: false; reason: "rejected" | "unavailable" | "rateLimited" }> {
    try {
      const res = await fetch(`${config.authUrl}/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firebaseToken, password: newPassword, displayName: name }),
      });
      if (res.ok) return { ok: true };
      // 429 is a transient throttle, NOT an authoritative rejection — surface a
      // retry message rather than the validation-failure copy. It must still not
      // fall back to Firebase (that would bypass the server password policy), so
      // it gets its own reason distinct from both "rejected" and "unavailable".
      if (res.status === 429) return { ok: false, reason: "rateLimited" };
      // A 4xx means the server validated the request and refused it (e.g. a
      // password that fails the shared policy). The server is authoritative —
      // do NOT fall back to writing the password directly via Firebase, which
      // would bypass that policy. Only a transient/unavailable backend (5xx)
      // is allowed to fall back below.
      if (res.status >= 400 && res.status < 500) return { ok: false, reason: "rejected" };
      return { ok: false, reason: "unavailable" };
    } catch (err) {
      Sentry.captureException(err, { tags: { flow: "set-password-backend" } });
      return { ok: false, reason: "unavailable" };
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const trimmedName = displayName.trim();
    if (nameProblem) {
      setNameTouched(true);
      document.getElementById("display-name")?.focus();
      return;
    }
    const weakness = checkPasswordStrength(password);
    if (weakness === "tooShort") {
      setError(t("auth.passwordTooShort"));
      return;
    }
    if (weakness === "tooLong") {
      setError(t("auth.passwordTooLong"));
      return;
    }
    if (weakness) {
      setError(t(WEAKNESS_MESSAGE_KEYS[weakness]));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.passwordMismatch"));
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not authenticated");

      const idToken = await user.getIdToken(true);
      const result = await setPasswordViaBackend(idToken, password, trimmedName);

      if (!result.ok) {
        if (result.reason === "rateLimited") {
          // Transient throttle — ask the user to retry. Do NOT fall back to
          // Firebase (that would bypass the server-enforced password policy).
          throw new Error(t("auth.tooManyRequests"));
        }
        if (result.reason === "rejected") {
          // Server is the source of truth for the password policy — surface the
          // rejection instead of silently setting a weaker password via Firebase.
          throw new Error(t("auth.setPasswordRejected"));
        }
        // Backend unavailable (network / 5xx): fall back to Firebase so the
        // invitee isn't hard-blocked by an outage. The password already passed
        // the same shared strength checks above.
        const hasPasswordProvider = user.providerData.some(
          (p) => p.providerId === "password",
        );
        if (hasPasswordProvider) {
          await updatePassword(user, password);
        } else {
          const credential = EmailAuthProvider.credential(user.email!, password);
          await linkWithCredential(user, credential);
        }
      }

      await onComplete(password);
    } catch (err) {
      Sentry.captureException(err, { tags: { flow: "set-password" } });
      const msg = err instanceof Error ? err.message : "Failed to set password";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-[var(--background)] p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1.5 text-start">
          <AdgeLogo className="mb-3 origin-start" aria-label={t("auth.brandName")} />
          <CardTitle className="text-2xl font-semibold tracking-tight">{t("auth.setPassword")}</CardTitle>
          <CardDescription className="text-sm leading-relaxed">
            {email ? t("auth.setPasswordForLabel") : t("auth.setPasswordDescription")}
            {email && (
              <span className="mt-1 block font-medium text-[var(--foreground)] break-all">
                {email}
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="display-name">{t("auth.yourName")}</Label>
              <Input
                id="display-name"
                type="text"
                placeholder={t("auth.yourNamePlaceholder")}
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setNameTouched(true); setError(null); }}
                onBlur={() => setNameTouched(true)}
                required
                autoFocus
                disabled={loading}
                maxLength={DISPLAY_NAME_MAX_LENGTH}
                autoComplete="name"
                aria-invalid={showNameError || undefined}
                aria-describedby={showNameError ? "display-name-error" : "display-name-help"}
              />
              {showNameError && nameErrorKey ? (
                <p id="display-name-error" role="alert" className="text-xs text-[var(--destructive)]">
                  {t(nameErrorKey)}
                </p>
              ) : (
                <p id="display-name-help" className="text-xs text-[var(--muted-foreground)]">
                  {t("auth.yourNameHelp")}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">{t("auth.newPassword")}</Label>
              <PasswordInput
                id="new-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(null); }}
                required
                minLength={PASSWORD_MIN_LENGTH}
                maxLength={PASSWORD_MAX_LENGTH}
                disabled={loading}
                autoComplete="new-password"
                aria-describedby="password-rules"
                showPasswordLabel={t("auth.showPassword")}
                hidePasswordLabel={t("auth.hidePassword")}
              />
              <PasswordStrengthMeter password={password} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t("auth.confirmPassword")}</Label>
              <PasswordInput
                id="confirm-password"
                placeholder="••••••••"
                value={confirm}
                onChange={(e) => { setConfirm(e.target.value); setError(null); }}
                required
                minLength={PASSWORD_MIN_LENGTH}
                maxLength={PASSWORD_MAX_LENGTH}
                disabled={loading}
                autoComplete="new-password"
                showPasswordLabel={t("auth.showPassword")}
                hidePasswordLabel={t("auth.hidePassword")}
              />
            </div>

            {/* Hidden until the user starts typing, then revealed with a smooth
                grid-rows collapse (height + fade). The negative margin cancels
                the form's space-y gap while collapsed so no empty space is
                reserved. */}
            <div
              className={cn(
                "grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out motion-reduce:transition-none",
                password.length > 0 ? "grid-rows-[1fr] opacity-100" : "-mt-4 grid-rows-[0fr] opacity-0",
              )}
              aria-hidden={password.length === 0}
            >
              <ul id="password-rules" className="min-h-0 space-y-1 overflow-hidden" aria-label={t("auth.passwordRequirements")}>
                {rules.map((r) => (
                  <RuleRow key={r.key} met={r.met} label={r.label} />
                ))}
              </ul>
            </div>

            {error && (
              <p role="alert" className="text-sm text-[var(--destructive)]">{error}</p>
            )}
            <Button type="submit" className="w-full gap-2" disabled={loading} aria-busy={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              <span>{loading ? t("auth.settingPassword") : t("auth.savePassword")}</span>
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
